'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import GfPromoCodes from '@/components/country/GfPromoCodes';
import GfCta from '@/components/common/GfCta';
import { t } from '@/i18n/translations';

/**
 * eSIM promo codes subpage — "Gallery · Field" design.
 * Masthead + category filter chips + .gf-promos grid (reveal/copy) + CTA + disclaimer.
 */
export default function ESimPromotionsContent({ content, lang = 'ru', country = 'montenegro' }) {
    const ui = t(lang, 'esimToolsUi').promos;
    const [category, setCategory] = useState('all');

    const categories = Array.isArray(content.categories) ? content.categories : [];

    const filtered = useMemo(() => {
        const promotions = Array.isArray(content.promotions) ? content.promotions : [];
        return category === 'all' ? promotions : promotions.filter((p) => p.category === category);
    }, [content.promotions, category]);

    const backHref = content.backHref || `/${lang}/${country}/esim`;
    const cName = content.countryName || (country ? country.charAt(0).toUpperCase() + country.slice(1) : '');
    const countLabel = ui.countLabel(filtered.length);

    return (
        <div className="gf-root">
            {/* ── MASTHEAD ─────────────────────────────────────── */}
            <section className="pr-mast">
                <Link className="pr-back" href={backHref}>{ui.back(cName)}</Link>
                <p className="pr-kick">{ui.kick(cName)}</p>
                <h1>{content.title || ui.title}</h1>
                {content.description && <p className="sub">{content.description}</p>}
            </section>

            {/* ── FILTER ───────────────────────────────────────── */}
            {categories.length > 0 && (
                <nav className="pr-filter" aria-label={ui.categoriesAria}>
                    <div className="pr-filter-in">
                        <div className="gf-ops" role="group">
                            <button type="button" className={`all ${category === 'all' ? 'on' : ''}`} onClick={() => setCategory('all')}>{ui.all}</button>
                            {categories.filter((c) => c.id !== 'all').map((c) => (
                                <button type="button" key={c.id} className={category === c.id ? 'on' : ''} onClick={() => setCategory(c.id)}>{c.name}</button>
                            ))}
                        </div>
                        <span className="pr-count">{countLabel}</span>
                    </div>
                </nav>
            )}

            {/* ── PROMOS ───────────────────────────────────────── */}
            <main className="pr-main">
                <div className="pr-secmark"><span className="no">{ui.secmark}</span><span className="r">{ui.current}</span></div>
                <GfPromoCodes
                    promotions={filtered}
                    lang={lang}
                    country={country}
                    vertical="esim"
                    emptyLabel={ui.empty}
                />
            </main>

            {/* ── CTA ──────────────────────────────────────────── */}
            <GfCta
                className="pr-cta"
                eyebrow={ui.ctaEyebrow}
                title={ui.ctaTitle}
            >
                <Link className="gf-btn" href={backHref}>{ui.ctaBtn}</Link>
            </GfCta>

            {/* ── DISCLAIMER ───────────────────────────────────── */}
            <div className="pr-note">
                <p className="pr-disclaim">{ui.disclaimer}</p>
            </div>
        </div>
    );
}
